import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Zap, Star, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import SkipLink from '../components/accessibility/SkipLink';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StoryView = ({ userId }) => {
  const { t, i18n } = useTranslation();
  const { scenarioId } = useParams();
  const navigate = useNavigate();
  const [scenario, setScenario] = useState(null);
  const [currentNode, setCurrentNode] = useState(null);
  const [choices, setChoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [transitioning, setTransitioning] = useState(false);
  const [earnedXP, setEarnedXP] = useState(0);
  const [isFavorited, setIsFavorited] = useState(false);

  useEffect(() => {
    fetchScenario();
  }, [scenarioId]);

  // Refetch scenario when language changes
  useEffect(() => {
    const handleLanguageChange = () => {
      fetchScenario();
    };
    
    i18n.on('languageChanged', handleLanguageChange);
    
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [i18n]);

  const fetchScenario = async () => {
    try {
      const currentLang = localStorage.getItem('preferred_language') || 'en';
      const response = await axios.get(`${API}/scenarios/${scenarioId}?lang=${currentLang}`);
      setScenario(response.data);
      setIsFavorited(response.data.favorited_by?.includes(userId) || false);
      
      // Check if scenario has complete nodes (needs at least 2 nodes for branching)
      if (!response.data.nodes || response.data.nodes.length < 2) {
        setLoading(false);
        return; // Will show "Coming Soon" message
      }
      
      const progressRes = await axios.get(`${API}/progress/${userId}`);
      const savedProgress = progressRes.data.find(p => p.scenario_id === scenarioId);
      
      // Support both node_id and id for backward compatibility
      const startNodeId = savedProgress?.current_node || response.data.entry_node_id || 'node_1';
      const node = response.data.nodes.find(n => (n.node_id || n.id) === startNodeId);
      
      // If node not found, show Coming Soon
      if (!node) {
        setLoading(false);
        return;
      }
      
      setCurrentNode(node);
      setChoices(savedProgress?.choices_made || []);
      setEarnedXP(savedProgress?.earned_xp || 0);
    } catch (error) {
      console.error('Error fetching scenario:', error);
      toast.error('Failed to load scenario');
    } finally {
      setLoading(false);
    }
  };

  const handleChoice = async (choice) => {
    setTransitioning(true);
    const newChoices = [...choices, choice.id];
    const newXP = earnedXP + (choice.xp_delta || 0);
    setChoices(newChoices);
    setEarnedXP(newXP);
    
    setTimeout(async () => {
      const nextNode = scenario.nodes.find(n => n.node_id === choice.next_node_id);
      setCurrentNode(nextNode);
      setTransitioning(false);
      
      try {
        // Check if this is a debrief (ending) node
        const isCompleted = nextNode?.content_type === 'debrief';
        
        await axios.post(`${API}/progress`, {
          user_id: userId,
          scenario_id: scenarioId,
          xp_earned: choice.xp_delta || 0,
          completed: isCompleted
        });
        
        if (choice.xp_delta > 0) {
          toast.success(`+${choice.xp_delta} XP!`, {
            description: 'Great decision',
            duration: 2000
          });
        }
      } catch (error) {
        console.error('Error saving progress:', error);
      }
    }, 300);
  };

  const toggleFavorite = async () => {
    try {
      if (isFavorited) {
        await axios.delete(`${API}/scenarios/${scenarioId}/favorite`, {
          params: { user_id: userId }
        });
        setIsFavorited(false);
        toast('Removed from favorites');
      } else {
        await axios.post(`${API}/scenarios/${scenarioId}/favorite`, null, {
          params: { user_id: userId }
        });
        setIsFavorited(true);
        toast.success('⭐ Saved to favorites!');
      }
    } catch (error) {
      console.error('Favorite error:', error);
      toast.error('Failed to update favorite');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen" role="status" aria-live="polite" aria-label="Loading scenario">
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className="text-xl font-semibold"
        >
          <Zap className="w-12 h-12 text-[#CCFF00]" aria-hidden="true" />
        </motion.div>
      </div>
    );
  }

  if (!currentNode) {
    // Scenario has no complete nodes - show Coming Soon
    return (
      <div className="min-h-screen bg-[#0F0E17] flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="bg-gradient-to-br from-ssdp-teal to-ssdp-purple p-8 rounded-3xl border-4 border-ssdp-navy max-w-md text-center shadow-2xl"
        >
          <Sparkles className="w-16 h-16 text-white mx-auto mb-4" />
          <h2 className="text-3xl font-black text-white mb-4 font-heading">
            Coming Soon!
          </h2>
          <p className="text-white/90 mb-6 font-body">
            <strong>{scenario?.title}</strong> is being developed. We&apos;re creating an immersive experience for this topic. Check back soon!
          </p>
          <Button
            onClick={() => navigate('/')}
            className="bg-white text-ssdp-navy hover:bg-gray-100 font-bold shadow-lg"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Dashboard
          </Button>
        </motion.div>
      </div>
    );
  }
  
  // Check if this is a debrief node (ending)
  const isDebrief = currentNode.content_type === 'debrief';

  // Detect reduced motion preference
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  return (
    <>
      <SkipLink />
      <div className="min-h-screen bg-[#0F0E17] relative overflow-hidden">
        {/* Comic background effect */}
        <div className="absolute inset-0 opacity-5" aria-hidden="true" style={{
          backgroundImage: 'repeating-linear-gradient(0deg, transparent, transparent 2px, #CCFF00 2px, #CCFF00 4px)',
          backgroundSize: '100% 4px'
        }} />

        <div className="relative h-screen flex flex-col">
          {/* Top Navigation */}
          <nav className="absolute top-4 sm:top-6 left-4 sm:left-6 right-4 sm:right-6 z-10 flex justify-between" aria-label="Story navigation">
            <motion.div
              {...(prefersReducedMotion ? {} : { whileHover: { scale: 1.1 }, whileTap: { scale: 0.9 } })}
            >
              <Button
                onClick={() => navigate('/')}
                className="bg-ssdp-teal text-black hover:bg-ssdp-teal/90 rounded-full font-bold shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] min-h-[44px] min-w-[44px] p-2 sm:p-3 focus:outline-none focus:ring-4 focus:ring-ssdp-teal focus:ring-offset-4"
                data-testid="back-button"
                aria-label="Back to dashboard"
              >
                <ArrowLeft className="w-5 h-5" aria-hidden="true" />
              </Button>
            </motion.div>
          </nav>

        <AnimatePresence mode="wait">
          <motion.div
            key={currentNode.node_id}
            initial={{ opacity: 0, x: 100 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -100 }}
            transition={{ type: "spring", stiffness: 100 }}
            className="relative flex-1"
          >
            {/* Comic Panel Container */}
            <div className="absolute inset-0">
              {/* Animated Image Background */}
              <motion.div
                animate={{
                  scale: [1, 1.05, 1],
                }}
                transition={{
                  duration: 10,
                  repeat: Infinity,
                  ease: "easeInOut"
                }}
                className="absolute inset-0"
              >
                <img
                  src={currentNode.content_image || scenario.thumbnail}
                  alt={scenario.title}
                  className="w-full h-full object-cover filter brightness-75"
                />
              </motion.div>
              
              {/* Comic-style gradient overlay */}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
              
              {/* Comic halftone effect */}
              <div className="absolute inset-0 opacity-10 mix-blend-overlay" style={{
                backgroundImage: 'radial-gradient(circle, #CCFF00 1px, transparent 1px)',
                backgroundSize: '10px 10px'
              }} />
            </div>

            {/* Content Panel - Comic Style */}
            <div className="relative h-full flex flex-col justify-end p-6 md:p-8">
              <motion.div
                initial={{ y: 50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2, type: "spring", stiffness: 100 }}
                className="bg-white/10 backdrop-blur-xl border-4 border-ssdp-navy rounded-3xl p-4 md:p-6 shadow-[8px_8px_0px_0px_rgba(0,128,128,0.3)] max-h-[85vh] overflow-y-auto overflow-x-hidden"
              >
                {/* Comic Title with animated underline */}
                <div className="relative mb-4">
                  <motion.h2
                    initial={{ scale: 0.9 }}
                    animate={{ scale: 1 }}
                    className="text-2xl md:text-3xl font-extrabold text-[#CCFF00] mb-2 uppercase tracking-tight font-['Outfit'] relative z-10"
                    style={{
                      textShadow: '3px 3px 0px #000, -1px -1px 0px #000, 1px -1px 0px #000, -1px 1px 0px #000'
                    }}
                  >
                    {scenario.title}
                  </motion.h2>
                  <motion.div
                    initial={{ width: 0 }}
                    animate={{ width: "100%" }}
                    transition={{ delay: 0.3, duration: 0.5 }}
                    className="h-1 bg-[#FF0080] rounded-full"
                  />
                </div>

                {/* Story Text - Comic Speech Bubble Style */}
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.4 }}
                  className="relative bg-white rounded-2xl p-5 mb-6 border-4 border-ssdp-navy shadow-[4px_4px_0px_0px_rgba(30,58,89,1)]"
                >
                  <p className="text-base md:text-lg text-ssdp-navy leading-relaxed font-['DM_Sans'] font-medium whitespace-pre-wrap break-words overflow-wrap-anywhere word-break-break-word max-w-full">
                    {currentNode.content_text}
                  </p>
                  {/* Speech bubble pointer */}
                  <div className="absolute -bottom-4 left-8 w-0 h-0 border-l-[20px] border-l-transparent border-r-[20px] border-r-transparent border-t-[20px] border-t-black" />
                  <div className="absolute -bottom-[14px] left-[21px] w-0 h-0 border-l-[17px] border-l-transparent border-r-[17px] border-r-transparent border-t-[17px] border-t-white" />
                </motion.div>

                {/* Debrief Section (Harm Reduction Tips) */}
                {isDebrief && currentNode.debrief && (
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 }}
                    className="space-y-4 mb-6"
                  >
                    {/* Harm Reduction Tips */}
                    {currentNode.debrief.harm_reduction_tips && (
                      <div className="bg-ssdp-teal/20 backdrop-blur-md border-4 border-[#CCFF00] rounded-2xl p-5 shadow-[4px_4px_0px_0px_rgba(204,255,0,0.5)]">
                        <h3 className="text-[#CCFF00] font-bold text-xl mb-3 font-['Outfit']">
                          💡 Harm Reduction Tips
                        </h3>
                        <ul className="space-y-2">
                          {currentNode.debrief.harm_reduction_tips.map((tip, idx) => (
                            <li key={idx} className="text-white text-sm leading-relaxed flex gap-2">
                              <span className="text-[#CCFF00] flex-shrink-0">•</span>
                              <span className="break-words overflow-wrap-anywhere">{tip}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* Reflection Questions */}
                    {currentNode.debrief.reflection_questions && (
                      <div className="bg-[#FF0080]/20 backdrop-blur-md border-4 border-[#FF0080] rounded-2xl p-5 shadow-[4px_4px_0px_0px_rgba(255,0,128,0.5)]">
                        <h3 className="text-[#FF0080] font-bold text-xl mb-3 font-['Outfit']">
                          🤔 Reflect
                        </h3>
                        <ul className="space-y-2">
                          {currentNode.debrief.reflection_questions.map((q, idx) => (
                            <li key={idx} className="text-white text-sm leading-relaxed flex gap-2">
                              <span className="text-[#FF0080] flex-shrink-0">•</span>
                              <span>{q}</span>
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {/* XP Summary */}
                    <div className="bg-white/20 backdrop-blur-md border-4 border-white rounded-2xl p-5 shadow-[4px_4px_0px_0px_rgba(255,255,255,0.5)]">
                      <div className="flex items-center justify-between">
                        <span className="text-white font-bold text-lg font-['Outfit']">Total XP Earned</span>
                        <div className="flex items-center gap-2">
                          <Zap className="w-6 h-6 text-[#CCFF00]" />
                          <span className="text-[#CCFF00] font-bold text-2xl font-['Outfit']">+{earnedXP}</span>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}

                {/* Choices or Ending */}
                {isDebrief ? (
                  <motion.div
                    {...(prefersReducedMotion ? {} : { initial: { scale: 0.8, opacity: 0 }, animate: { scale: 1, opacity: 1 }, transition: { delay: 0.6 } })}
                    className="space-y-3"
                    role="region"
                    aria-label="Scenario completed"
                  >
                    {/* Show Favorite button for generated scenarios */}
                    {scenario.is_generated && (
                      <Button
                        onClick={toggleFavorite}
                        className={`w-full font-bold px-8 py-4 rounded-xl border-4 border-ssdp-navy shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:shadow-[2px_2px_0px_0px_rgba(30,58,89,1)] transition-all min-h-[44px] focus:outline-none focus:ring-4 focus:ring-offset-4 ${
                          isFavorited
                            ? 'bg-[#FF0080] text-white hover:bg-[#FF0080]/90 focus:ring-[#FF0080]'
                            : 'bg-white text-ssdp-navy hover:bg-gray-50 focus:ring-ssdp-navy'
                        }`}
                        data-testid="favorite-button"
                        aria-pressed={isFavorited}
                        aria-label={isFavorited ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <Star className={`w-5 h-5 mr-2 ${isFavorited ? 'fill-white' : ''}`} aria-hidden="true" />
                        {isFavorited ? 'Saved to Favorites' : 'Save to Favorites'}
                      </Button>
                    )}
                    <Button
                      onClick={() => navigate('/')}
                      className="w-full bg-ssdp-teal text-black border-4 border-ssdp-navy font-bold px-8 py-4 rounded-xl hover:bg-ssdp-teal/90 shadow-[4px_4px_0px_0px_rgba(30,58,89,1)] hover:shadow-[2px_2px_0px_0px_rgba(30,58,89,1)] transition-all min-h-[44px] focus:outline-none focus:ring-4 focus:ring-ssdp-teal focus:ring-offset-4"
                      data-testid="back-to-dashboard-button"
                      aria-label="Return to dashboard"
                    >
                      Back Home
                    </Button>
                  </motion.div>
                ) : (
                  <div className="space-y-3" role="group" aria-label="Story choices">
                    {currentNode.choices?.map((choice, index) => (
                      <motion.div
                        key={choice.id}
                        {...(prefersReducedMotion ? {} : { initial: { x: -50, opacity: 0 }, animate: { x: 0, opacity: 1 }, transition: { delay: 0.5 + index * 0.1, type: "spring" } })}
                      >
                        <Button
                          onClick={() => handleChoice(choice)}
                          disabled={transitioning}
                          className={`w-full bg-white/20 backdrop-blur-md text-white font-bold px-6 py-5 rounded-xl border-4 ${
                            choice.xp_delta > 20 ? 'border-[#CCFF00] hover:bg-ssdp-teal/30 focus:ring-[#CCFF00]' : 'border-white/50 hover:bg-white/30 focus:ring-white'
                          } transition-all text-left h-auto shadow-[4px_4px_0px_0px_rgba(0,0,0,0.5)] hover:shadow-[2px_2px_0px_0px_rgba(0,0,0,0.5)] hover:translate-x-[2px] hover:translate-y-[2px] disabled:opacity-50 disabled:cursor-not-allowed font-['DM_Sans'] min-h-[44px] focus:outline-none focus:ring-4 focus:ring-offset-4`}
                          data-testid={`choice-${choice.id}`}
                          aria-label={`${choice.text}${choice.xp_delta > 0 ? `. Earn ${choice.xp_delta} XP` : ''}`}
                          aria-disabled={transitioning}
                        >
                          <div className="flex justify-between items-start gap-3 w-full">
                            <span className="break-words overflow-wrap-anywhere word-break-break-word flex-1 max-w-full">{choice.text}</span>
                            {choice.xp_delta > 0 && (
                              <span className="text-[#CCFF00] text-sm flex-shrink-0 flex items-center gap-1" aria-hidden="true">
                                <Zap className="w-4 h-4" />
                                +{choice.xp_delta}
                              </span>
                            )}
                          </div>
                        </Button>
                      </motion.div>
                    ))}
                  </div>
                )}
              </motion.div>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
    </>
  );
};

export default StoryView;
